import { devices } from '@evidia/components';
import type { ArrayElement, DrivingLessonRecordState } from '@evidia/shared';
import { DrivingLessonRecordType, StudentDefaultTeacherType, toCZHoursString, toCzDateString } from '@evidia/shared';
import { Button, Space } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useActiveSchoolId } from '../../../../hooks/useActiveSchoolId';
import { getDrivingLessonRecordTypeName } from '../../../../utils/naming';
import type { RouterOutput } from '../../../../utils/trcp';
import { trpc } from '../../../../utils/trcp';
import DefaultTeacher from '../../../DefaultTeacher';
import SimpleDrawer from '../../../SimpleDrawer';
import SimpleTable from '../../../SimpleTable/SimpleTable';
import { useDrivingLessonsData } from '../../hooks/useDrivingLessonsData';
import { resolveDrivingDataLessonsTitle } from '../../utils';
import { ColumnLabel, EvidenceHeaderContainer, TabTitle } from '../styles';
import ActiveTrainingPlan from './ActiveTrainingPlan';
import StatusIcon from './StatusIcon';
import { TrainingItem } from './TrainingItem';
import TrainingPlan from './TrainingPlan';

export type DrivingLessonsRecord = ArrayElement<RouterOutput['drivingLessonsRecord']['getDrivingLessonsRecords']>;

const SIGNATURE_FIELDS = [
	{
		title: 'Podpis Žáka',
		render: (item: DrivingLessonsRecord) => {
			if (item.signature) {
				return <StatusIcon status="OK" />;
			}

			return <StatusIcon status="NOK" tooltip="Jízda není podepsána" />;
		},
	},
	{
		title: 'Podpis Učitele',
		render: (item: DrivingLessonsRecord) => {
			if (item.teacherSignature) {
				return <StatusIcon status="OK" />;
			}

			return <StatusIcon status="NOK" tooltip="Jízda není podepsána" />;
		},
	},
];

const NoTopPaddingDrawer = styled(SimpleDrawer)`
	.ant-drawer-body {
		padding-top: 10px;
	}

	@media ${devices.tablet} {
		padding-bottom: 0;
	}
`;

const TableWrapper = styled.div`
	@media ${devices.tablet} {
		margin-left: -24px;
		width: calc(100% + 48px);

		h1 {
			margin-left: 24px;
		}
	}

	h1 {
		font-style: normal;
		font-weight: 500;
		font-size: 20px;
		line-height: 28px;
		padding-bottom: 1em;
	}
`;

const Buttons = styled.div`
	display: flex;
	gap: 10px;

	@media ${devices.tablet} {
		flex-direction: column;
	}
`;

interface Props {
	studentId: string;
}

const TrainingTab: React.FC<Props> = ({ studentId }) => {
	const { activeSchoolId } = useActiveSchoolId();
	const [evidenceDrawerVisible, setEvidenceDrawerVisible] = useState(false);
	const [planDrawerVisible, setPlanDrawerVisible] = useState(false);
	// const [activeDrivingLessonsRecord, setActiveDrivingLessonsRecord] = useState<DrivingLessonsRecord | undefined>();
	const [activeDrivingLessonsEditRecord, setActiveDrivingLessonsEditRecord] = useState<
		DrivingLessonsRecord | undefined
	>();
	const getDefaultTeacher = trpc.studentDefaultTeacher.getDefaultTeacher.useQuery({
		schoolId: activeSchoolId,
		studentId,
		type: StudentDefaultTeacherType.TRAINING,
	});
	const getStudentDateOfInProgress = trpc.student.getStudentDateOfInProgress.useQuery({
		schoolId: activeSchoolId,
		studentId,
	});
	const getStudentDateOfFinished = trpc.student.getStudentDateOfFinished.useQuery({
		schoolId: activeSchoolId,
		studentId,
	});

	const {
		isLoading: drivingLessonsDataLoading,
		drivingLessonsData,
		refetch: refetchDrivingLessonsData,
	} = useDrivingLessonsData(activeSchoolId, studentId);

	const dataLoading = getDefaultTeacher.isLoading;
	const isLoading = drivingLessonsDataLoading || dataLoading;

	return (
		<>
			<TabTitle>Evidence výcviku</TabTitle>
			<EvidenceHeaderContainer>
				<Space direction="vertical">
					<ColumnLabel>Datum zahájení</ColumnLabel>
					<div>{getStudentDateOfInProgress.data ? toCzDateString(getStudentDateOfInProgress.data) : '-'}</div>
				</Space>
				<Space direction="vertical">
					<ColumnLabel>Datum ukončení</ColumnLabel>
					<div>{getStudentDateOfFinished.data ? toCzDateString(getStudentDateOfFinished.data) : '-'}</div>
				</Space>
				<DefaultTeacher
					studentId={studentId}
					type={StudentDefaultTeacherType.TRAINING}
					defaultTeacher={getDefaultTeacher.data}
					refetch={getDefaultTeacher.refetch as unknown as () => Promise<void>}
				/>
			</EvidenceHeaderContainer>

			<TableWrapper>
				<h1>Jízdy</h1>
				<SimpleTable
					border
					tabletSmooth
					count={{
						data: drivingLessonsData[DrivingLessonRecordType.DRIVING]?.length ?? 0,
						loading: isLoading,
					}}
					data={{
						data: drivingLessonsData[DrivingLessonRecordType.DRIVING] ?? [],
						loading: isLoading,
					}}
					columns={[
						{
							title: 'Datum',
							render: (item: DrivingLessonsRecord) => {
								const { title, hasError } = resolveDrivingDataLessonsTitle(item);

								return (
									<Space direction="vertical">
										<span style={{ color: hasError ? 'red' : '#595959' }}>{title}</span>
										<span>{toCzDateString(item.startTime)}</span>
									</Space>
								);
							},
						},
						{
							title: 'Čas',
							render: (item: DrivingLessonsRecord) => (
								<span>{`${toCZHoursString(item.startTime)} - ${toCZHoursString(item.endTime)}`}</span>
							),
						},
						{
							title: 'Délka',
							render: (item: DrivingLessonsRecord) => <span>{`${item.length} minut`}</span>,
						},
						{
							title: 'Učitel',
							render: (item: DrivingLessonsRecord) => {
								if (item.user) {
									return `${item.user.firstName} ${item.user.lastName}`;
								}

								return <StatusIcon status="NOK" tooltip="Učitel není přiřazen" />;
							},
						},
						{
							title: 'Vozidlo',
							render: (item: DrivingLessonsRecord) => {
								if (item.schoolCar) {
									return `${item.schoolCar.carType} ${item.schoolCar.regNumber}`;
								}

								return <StatusIcon status="NOK" tooltip="Vozidlo není přiřazeno" />;
							},
						},
						{
							title: 'Ujeto',
							render: (item: DrivingLessonsRecord) => {
								if (item.tachometerBefore && item.tachometerAfter) {
									return `${item.tachometerAfter - item.tachometerBefore} km`;
								}

								return <StatusIcon status="NOK" tooltip="Jízda není zaevidována" />;
							},
						},
						...SIGNATURE_FIELDS,
					]}
					onRowClick={(row: DrivingLessonsRecord) => {
						setActiveDrivingLessonsEditRecord(row);
					}}
					renderResponsiveContent={record => (
						<TrainingItem
							car={record.schoolCar?.carType ?? ''}
							lecturer={record.user?.lastName ?? ''}
							date={record.startTime}
							lessons={record.lessonHours}
							state={record.state as DrivingLessonRecordState}
						/>
					)}
				/>
			</TableWrapper>
			{[DrivingLessonRecordType.PV_UV, DrivingLessonRecordType.PV_ZP].map(type => (
				<TableWrapper key={type}>
					<h1>{getDrivingLessonRecordTypeName(type)}</h1>
					<SimpleTable
						border
						tabletSmooth
						count={{
							data: drivingLessonsData[type]?.length ?? 0,
							loading: isLoading,
						}}
						data={{
							data: drivingLessonsData[type] ?? [],
							loading: isLoading,
						}}
						columns={[
							{
								title: 'Datum',
								render: (item: DrivingLessonsRecord) => {
									const { title, hasError } = resolveDrivingDataLessonsTitle(item, 'Lekce');

									return (
										<Space direction="vertical">
											<span style={{ color: hasError ? 'red' : '#595959' }}>{title}</span>
											<span>{toCzDateString(item.startTime)}</span>
										</Space>
									);
								},
							},
							{
								title: 'Čas',
								render: (item: DrivingLessonsRecord) => (
									<span>{`${toCZHoursString(item.startTime)} - ${toCZHoursString(item.endTime)}`}</span>
								),
							},
							{
								title: 'Učitel',
								render: (item: DrivingLessonsRecord) => {
									if (item.user) {
										return `${item.user.firstName} ${item.user.lastName}`;
									}

									return <StatusIcon status="NOK" tooltip="Učitel není přiřazen" />;
								},
							},
							...SIGNATURE_FIELDS,
						]}
						onRowClick={(row: DrivingLessonsRecord) => {
							setActiveDrivingLessonsEditRecord(row);
						}}
						renderResponsiveContent={record => (
							<TrainingItem
								// TODO
								car={record.schoolCar?.carType ?? ''}
								lecturer={record.user?.lastName ?? ''}
								date={record.startTime}
								lessons={record.lessonHours}
								state={record.state as DrivingLessonRecordState}
							/>
						)}
					/>
				</TableWrapper>
			))}
			<Buttons>
				<Button size="large" onClick={() => setEvidenceDrawerVisible(true)} loading={dataLoading}>
					Naplánovat výcvik
				</Button>

				<Button size="large" type="primary" onClick={() => setPlanDrawerVisible(true)} loading={dataLoading}>
					Zaevidovat výcvik
				</Button>
			</Buttons>
			<NoTopPaddingDrawer
				data-testid="training-plan-drawer"
				open={evidenceDrawerVisible}
				onClose={() => setEvidenceDrawerVisible(false)}
			>
				<TrainingPlan
					studentId={studentId}
					onClose={() => {
						setEvidenceDrawerVisible(false);
						refetchDrivingLessonsData();
					}}
					drivingLessonData={drivingLessonsData}
					defaultTeacher={getDefaultTeacher.data}
					type="plan"
				/>
			</NoTopPaddingDrawer>
			<NoTopPaddingDrawer
				data-testid="training-plan-drawer"
				open={planDrawerVisible}
				onClose={() => setPlanDrawerVisible(false)}
			>
				<TrainingPlan
					studentId={studentId}
					onClose={() => {
						setPlanDrawerVisible(false);
						refetchDrivingLessonsData();
					}}
					drivingLessonData={drivingLessonsData}
					defaultTeacher={getDefaultTeacher.data}
					type="create"
				/>
			</NoTopPaddingDrawer>
			<NoTopPaddingDrawer
				open={!!activeDrivingLessonsEditRecord}
				onClose={() => setActiveDrivingLessonsEditRecord(undefined)}
			>
				<ActiveTrainingPlan
					activeDrivingLessonsRecord={activeDrivingLessonsEditRecord}
					studentId={studentId}
					onClose={() => {
						setActiveDrivingLessonsEditRecord(undefined);
						refetchDrivingLessonsData();
					}}
					isEdit
				/>
			</NoTopPaddingDrawer>
		</>
	);
};

export default TrainingTab;
